/**
 * Screen — React (web) implementation.
 *
 * Two sizing models (see `ScreenLayout`): `fill` is a full-height container
 * with an optional internal scroll region and a pinned footer; `page` grows to
 * its content with no inner scroll region and a footer that flows after the
 * content, for web pages that already carry their own chrome.
 * Announces accessibilityTitle via aria-live on mount.
 * No raw hex values or pixel sizes in this file.
 */

import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { colors } from '../../elements/colors';
import { spacing, SCREEN_GUTTER_KEY } from '../../elements/spacing';
import {
  screenContentPaddingTopValue,
  screenFooterBottomPaddingValue,
  SCREEN_CONTENT_FADE_HEIGHT,
  type ScreenProps,
} from './Screen';

/**
 * How a web Screen sizes itself against its host (web-only; not part of the
 * shared catalog schema, so authored GenUI documents cannot set it).
 *
 * - `fill` (default): fill the host's sized flex container; the content region
 *   scrolls internally under a pinned footer with a fade — the mobile model.
 * - `page`: grow to the content's height with no inner scroll region and no
 *   fade; the footer flows after the content and the document scrolls. This is
 *   the model for a web page that already carries its own header and footer
 *   chrome around the hosted screen.
 */
export type ScreenLayout = 'fill' | 'page';

const ScreenLayoutContext = createContext<ScreenLayout | null>(null);

export interface ScreenLayoutProviderProps {
  layout: ScreenLayout;
  children?: ReactNode;
}

/**
 * Selects the layout mode for every `Screen` rendered beneath it that does not
 * set `layout` itself. A host that renders screens through the json-render
 * registry (which passes only schema-validated document props) uses this to
 * choose the page-flow model without touching the shared spec.
 */
export function ScreenLayoutProvider({ layout, children }: ScreenLayoutProviderProps) {
  return <ScreenLayoutContext.Provider value={layout}>{children}</ScreenLayoutContext.Provider>;
}

/**
 * Screen background. `container` (white) is the default every existing screen
 * already renders; `primary` is the grey the tabbed surfaces use, set here so a
 * screen that belongs to one of them matches it without each child painting its
 * own panel and leaving the footer white.
 *
 * Mirrors the identical type in `Screen.native.tsx` — the two platform files
 * each declare their own props, so the union is stated in both rather than
 * imported across the platform split.
 */
export type ScreenBackground = 'container' | 'primary';

export interface WebScreenProps extends Partial<ScreenProps> {
  background?: ScreenBackground;
  screenId: string;
  accessibilityTitle: string;
  children?: ReactNode;
  /**
   * Standalone screens own the whole viewport (`100dvh`); the default fills
   * whatever sized flex container the host provides, so page chrome (site
   * header/footer) around a flow-hosted screen stays visible.
   */
  standalone?: boolean;
  /**
   * Sizing model against the host: `fill` (default) or `page`. Resolved as the
   * explicit prop, else the nearest `ScreenLayoutProvider`, else `fill`.
   * `standalone` takes precedence over either.
   */
  layout?: ScreenLayout;
  'data-testid'?: string;
}

export function Screen({
  accessibilityTitle,
  scrollable = true,
  hasFooter = false,
  contentPaddingHorizontal = SCREEN_GUTTER_KEY,
  footerPaddingHorizontal = SCREEN_GUTTER_KEY,
  footerDivider = true,
  footerFade = true,
  contentPaddingTop = 'm',
  footerBottomPadding = 'm',
  standalone = false,
  layout: layoutProp,
  children,
  background = 'container',
  'data-testid': testId,
}: WebScreenProps) {
  // The fade has to end in the screen's own colour, or it resolves to white
  // over a grey screen and reads as a band above the footer.
  const backgroundColor = colors.background[background];
  const [announced, setAnnounced] = useState(false);
  const layoutFromHost = useContext(ScreenLayoutContext);
  const layout: ScreenLayout = layoutProp ?? layoutFromHost ?? 'fill';
  // Page layout only applies to hosted screens; a standalone screen still
  // owns the viewport regardless of the host's preference.
  const pageLayout = !standalone && layout === 'page';

  useEffect(() => {
    // Reset then set so the live region fires on every accessibilityTitle change.
    setAnnounced(false);
    const id = requestAnimationFrame(() => setAnnounced(true));
    return () => cancelAnimationFrame(id);
  }, [accessibilityTitle]);

  const childArray = React.Children.toArray(children);
  const footerChild = hasFooter && childArray.length > 0 ? childArray[childArray.length - 1] : null;
  const contentChildren = hasFooter && childArray.length > 0 ? childArray.slice(0, -1) : childArray;

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    // Fill the host's sized flex container by default so surrounding page
    // chrome stays visible; standalone screens keep the full viewport; page
    // layout sets no size at all so the container grows to its content.
    ...(standalone
      ? { height: '100dvh' }
      : pageLayout
        ? {}
        : { flex: 1, minHeight: 0, height: '100%' }),
    backgroundColor,
  };

  const contentStyle: React.CSSProperties = {
    paddingLeft: spacing[contentPaddingHorizontal],
    paddingRight: spacing[contentPaddingHorizontal],
    paddingTop: screenContentPaddingTopValue(contentPaddingTop),
    // In page layout the region grows to its content and overflow stays
    // visible: the document, not this region, is what scrolls.
    ...(pageLayout
      ? {}
      : {
          flex: 1,
          minHeight: 0,
          ...(scrollable ? { overflowY: 'auto' as const } : { overflow: 'hidden' as const }),
        }),
  };

  const footerStyle: React.CSSProperties = {
    flexShrink: 0,
    paddingLeft: spacing[footerPaddingHorizontal],
    paddingRight: spacing[footerPaddingHorizontal],
    paddingTop: spacing.m,
    paddingBottom: screenFooterBottomPaddingValue(footerBottomPadding),
    ...(footerDivider ? { borderTop: `1px solid ${colors.stroke.tertiary}` } : {}),
  };

  // Anchors the fade to the content viewport (not the scrolling content) so it
  // stays put while content scrolls under the pinned footer.
  const contentWrapperStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    ...(pageLayout ? {} : { flex: 1, minHeight: 0 }),
  };

  // No fade in page layout: nothing scrolls under the footer, so there is
  // nothing to dissolve.
  const showFooterFade = !pageLayout && hasFooter && footerFade && scrollable;
  const fadeStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: SCREEN_CONTENT_FADE_HEIGHT,
    background: `linear-gradient(to bottom, ${colors.nav.gradientStart}, ${backgroundColor})`,
    pointerEvents: 'none',
  };

  return (
    <div
      style={containerStyle}
      data-testid={testId}
      // Exposes the resolved sizing model to hosts and their tests: registry-
      // rendered screens carry no test id of their own, so this is the one
      // stable hook for asserting which model a hosted screen ended up in.
      data-galapagos-screen-layout={standalone ? 'standalone' : layout}
    >
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}
      >
        {announced ? accessibilityTitle : ''}
      </div>
      <div style={contentWrapperStyle}>
        <div style={contentStyle} data-testid={testId ? `${testId}-content` : undefined}>
          {contentChildren}
        </div>
        {showFooterFade ? (
          <div
            style={fadeStyle}
            aria-hidden
            data-testid={testId ? `${testId}-footer-fade` : undefined}
          />
        ) : null}
      </div>
      {footerChild && (
        <div style={footerStyle} data-testid={testId ? `${testId}-footer` : undefined}>
          {footerChild}
        </div>
      )}
    </div>
  );
}
